import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import type { PolicySpec, StopSwitch } from '@reef/schema';
import {
  announceSignedIn,
  api,
  reloadWhenSignedIn,
  startOktaSignIn,
  type ConnectionSummary,
  type FactorySummary,
  type InboxResponse,
  type WorkflowSummary,
} from './api.ts';
import { isTab, parseRoute, pathFor, screenOnly, SECTIONS, type Route, type Tab } from './routes.ts';
import { CommandPalette, type Command } from './components/CommandPalette.tsx';
import { Icon, type IconName } from './components/Icon.tsx';
import { PaneResizer, usePaneWidth, useRailCollapsed } from './components/PaneResizer.tsx';
import { AgentsView } from './views/AgentsView.tsx';
import { ConnectionsView } from './views/ConnectionsView.tsx';
import { DeviceView } from './views/DeviceView.tsx';
import { FactoriesView } from './views/FactoriesView.tsx';
import { HomeView } from './views/HomeView.tsx';
import { InboxView } from './views/InboxView.tsx';
import { INSIGHT_SECTIONS, InsightsView } from './views/InsightsView.tsx';
import { RunsView } from './views/RunsView.tsx';
import { SETTINGS_SECTIONS, SettingsView } from './views/SettingsView.tsx';
import { WorkflowsView } from './views/WorkflowsView.tsx';
import { WorkView } from './views/WorkView.tsx';

interface Session {
  userId: string;
  email: string;
  /** Membership of `reef-admins`, resolved live. Decides which controls render at all. */
  isAdmin: boolean;
}

/**
 * The rail: ten destinations in three groups.
 *
 * Twenty-three buttons in six groups was a table of contents, and it made the
 * studio read as a set of admin screens with a factory somewhere in it. The
 * three groups now are the three things a person comes here to do — look at
 * the factory and act on what it needs, build the pieces it is made of, and
 * operate the deployment — and everything that lost a rail button became a
 * section of the screen it belongs to, at the same URL it always had.
 */
const NAV: ReadonlyArray<{
  group: string;
  items: ReadonlyArray<{ tab: Tab; label: string; icon: IconName; hint: string }>;
}> = [
  {
    group: 'Factory',
    items: [
      { tab: 'home', label: 'Home', icon: 'home', hint: 'What needs you, and what is running' },
      { tab: 'factories', label: 'Factories', icon: 'factory', hint: 'The lifecycles and their stages' },
      { tab: 'work', label: 'Work', icon: 'work', hint: 'Every item, in the stage it has reached' },
      { tab: 'inbox', label: 'Inbox', icon: 'inbox', hint: 'Decisions waiting on a person' },
    ],
  },
  {
    group: 'Build',
    items: [
      { tab: 'build', label: 'Workflows', icon: 'flow', hint: 'Author the stages' },
      { tab: 'agents', label: 'Agents', icon: 'agent', hint: 'What each agent does, and its instructions' },
      { tab: 'connections', label: 'Connections', icon: 'link', hint: 'What the factory can reach' },
    ],
  },
  {
    group: 'Operate',
    items: [
      { tab: 'runs', label: 'Runs', icon: 'play', hint: 'Every run and every step conversation' },
      { tab: 'insights', label: 'Insights', icon: 'chart', hint: 'Analytics, audit, the platform' },
      { tab: 'settings', label: 'Settings', icon: 'sliders', hint: 'Policies, ceilings, the stop switch' },
    ],
  },
];

/** The screen a rail item stands for, when the URL names no section. */
function sectionShown(route: Route): string {
  return route.section ?? SECTIONS[route.tab]?.[0] ?? '';
}

type Theme = 'system' | 'light' | 'dark';

/**
 * The colour scheme, as a preference rather than only as what the OS says.
 *
 * Written onto the root element so the stylesheet's `[data-theme]` blocks
 * win over `prefers-color-scheme`; `system` removes the attribute and the
 * media query decides again. Persisted per browser — it is a comfort, not
 * a setting anybody else should see.
 */
function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      const stored = localStorage.getItem('reef.theme');
      return stored === 'light' || stored === 'dark' ? stored : 'system';
    } catch {
      return 'system';
    }
  });
  useEffect(() => {
    if (theme === 'system') delete document.documentElement.dataset.theme;
    else document.documentElement.dataset.theme = theme;
    try {
      localStorage.setItem('reef.theme', theme);
    } catch {
      // A browser that refuses storage still gets the theme for this load.
    }
  }, [theme]);
  return [theme, setTheme];
}

export function App() {
  /*
   * The URL decides. Read from the path rather than defaulted, because a link
   * into the studio is usually somebody else's: Slack posts
   * `/workflows/:id/runs/:runId` when a run wants attention, and until this
   * was parsed that link opened the builder on an unrelated workflow and
   * reported nothing wrong.
   */
  const [route, setRoute] = useState<Route>(() =>
    parseRoute(window.location.pathname, window.location.search),
  );
  const tab = route.tab;
  const [session, setSession] = useState<Session | null>(null);
  const [workflows, setWorkflows] = useState<WorkflowSummary[]>([]);
  const [connections, setConnections] = useState<ConnectionSummary[]>([]);
  const [factories, setFactories] = useState<FactorySummary[]>([]);
  const [policies, setPolicies] = useState<PolicySpec[]>([]);
  const [skills, setSkills] = useState<string[]>([]);
  /**
   * Everything waiting on this person, as the one list `/inbox` returns.
   *
   * Held whole rather than as a count: Home lists the items and the Inbox's
   * All section is the same list, so one request every thirty seconds keeps
   * the badge, Home and the section agreeing rather than each fetching its own
   * copy and disagreeing by one for a moment after a decision.
   */
  const [inbox, setInbox] = useState<InboxResponse | null>(null);
  /** The deployment-wide stop switch, when one is engaged. Null is the normal state. */
  const [stopped, setStopped] = useState<StopSwitch | null>(null);
  const [fatal, setFatal] = useState<string | null>(null);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [theme, setTheme] = useTheme();

  /*
   * The back button, and a second link opened into a studio that is already up.
   * Both arrive as `popstate`, and both mean the same thing: the path moved, so
   * re-read it. Nothing else in the studio touches history, so this is the only
   * place the URL wins over what is on screen.
   */
  useEffect(() => {
    const onPopState = () =>
      setRoute(parseRoute(window.location.pathname, window.location.search));
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  /*
   * A tab that stood aside while another signed in has no way to notice: the
   * session cookie changed under it and nothing re-renders on a cookie. This
   * picks up the other tab's announcement and reloads, so the studio comes back
   * on its own rather than looking like the sign-in failed.
   */
  useEffect(() => reloadWhenSignedIn(), []);

  /** ⌘K / Ctrl+K opens the palette from anywhere that is not a text field. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((open) => !open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /**
   * Go somewhere, and say so in the address bar.
   *
   * `replace` is for a move *within* a screen — picking another run — because
   * pushing one entry per click would make the back button walk the run list
   * backwards one row at a time before it ever left the screen. Changing screen
   * pushes, which is what makes back mean "the screen I was on".
   */
  const go = useCallback((next: Partial<Route> & { tab: Tab }, replace = false) => {
    const full: Route = { ...screenOnly(next.tab), ...next };
    /*
     * Same route, same object. The screens that report where they are do it from
     * an effect, so they say it again on every render that touched anything
     * else; handing back a fresh object each time would re-render the whole
     * shell for a move that did not happen.
     */
    setRoute((previous) =>
      previous.tab === full.tab &&
      previous.section === full.section &&
      previous.workflowId === full.workflowId &&
      previous.runId === full.runId &&
      previous.connectionId === full.connectionId &&
      previous.factoryId === full.factoryId &&
      previous.item === full.item &&
      // Left out, a move from one step's chat to another of the same run would
      // be taken for no move — the path would change and the screen would not.
      previous.stepId === full.stepId
        ? previous
        : full,
    );
    const path = pathFor(full);
    // The query is part of the path here — `?item=` is written by `pathFor` —
    // so the comparison has to include it or a move to another row of the same
    // section would be taken for no move at all.
    if (path === window.location.pathname + window.location.search) return;
    // `state` is deliberately null: the route is derived from the path, so a
    // history entry carrying its own copy is a second source of truth that
    // drifts the first time somebody edits the URL by hand.
    window.history[replace ? 'replaceState' : 'pushState'](null, '', path);
  }, []);

  const openTab = useCallback((next: Tab) => go(screenOnly(next)), [go]);
  /** A section within the current screen is a move within it: replace, do not push. */
  const openSection = useCallback((section: string) => go({ tab, section }, true), [go, tab]);

  /** Which run the runs screen has open, kept in the URL so it can be shared back. */
  const openRun = useCallback(
    (workflowId: string | null, runId: string | null) => go({ tab: 'runs', workflowId, runId }, true),
    [go],
  );

  /** The same, for the builder — `/workflows/:id` is what a saved workflow links to. */
  const openWorkflow = useCallback((workflowId: string) => go({ tab: 'build', workflowId }, true), [go]);

  /** And for the connection being read, which is where an OAuth return lands. */
  const openConnection = useCallback(
    (connectionId: string) => go({ tab: 'connections', connectionId }, true),
    [go],
  );

  /**
   * The builder, arrived at from another screen.
   *
   * Pushes where `openWorkflow` replaces, and the difference is what back
   * means. Picking another workflow *in* the builder is a move within one
   * screen, so replacing is right there; following a workflow out of the
   * connection you were reading is leaving that screen, and replacing would
   * make back skip past it to whatever you were looking at before.
   */
  const leaveForWorkflow = useCallback((workflowId: string) => go({ tab: 'build', workflowId }), [go]);

  const refreshWorkflows = useCallback(() => {
    api
      .listWorkflows()
      .then((result) => setWorkflows(result.workflows))
      .catch((error) => setFatal((error as Error).message));
  }, []);

  const refreshFactories = useCallback(() => {
    api
      .factories()
      .then((result) => setFactories(result.factories))
      .catch(() => {
        // The factory list failing is reported by the screens that show it;
        // the shell only needs it for the palette and the home page.
      });
  }, []);

  /*
   * Read here rather than by the screens themselves, because the badge has to
   * be right on every screen — somebody looking at Runs is exactly the person
   * who needs to know a step has stopped and is waiting for them. A list that
   * cannot be fetched is not worth a banner over the whole shell; the screen
   * behind it reports the failure when opened.
   *
   * Sequenced like the connection list, and for the same reason: Home decides
   * an item and asks for a refresh, and a poll that left a moment earlier can
   * land after it carrying the row that was just approved. That reads as the
   * approval having come undone.
   */
  const inboxRequest = useRef(0);
  const refreshInbox = useCallback(() => {
    const requestNumber = ++inboxRequest.current;
    api
      .inbox()
      .then((result) => {
        if (requestNumber === inboxRequest.current) setInbox(result);
      })
      .catch(() => {});
  }, []);

  /*
   * Sequenced, because these responses can land out of order. The list is
   * re-fetched after every write, and a slower fetch issued *before* that write
   * would otherwise install pre-write data over the top of it. That reads to
   * the person at the screen as a save that silently came undone a moment after
   * it landed, which is worse than a save that visibly failed.
   */
  const connectionsRequest = useRef(0);
  const refreshConnections = useCallback(() => {
    const requestNumber = ++connectionsRequest.current;
    api
      .listConnections()
      .then((result) => {
        if (requestNumber === connectionsRequest.current) setConnections(result.connections);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/auth/get-session', { credentials: 'include' })
      .then((response) => (response.ok ? response.json() : null))
      .then(async (body: { user?: { id: string; email: string } } | null) => {
        /*
         * Back to the path that was asked for, not to `/`. Somebody following a
         * run link from Slack with no session gets sent to Okta first, and
         * returning them to the front door dropped the run they clicked —
         * `request` already preserves the path on a 401 for exactly this
         * reason, and this is the same case one step earlier.
         */
        if (!body?.user) return startOktaSignIn(window.location.pathname);

        // Tell the tabs that stood aside. They cannot see the cookie change
        // under them, so without this a tab that lost the sign-in race sits on
        // "redirecting to sign in" until somebody reloads it by hand.
        announceSignedIn();

        // better-auth's session says nothing about the reef-admins group, and
        // the UI has to know before it renders controls the API would refuse.
        const me = await api.me().catch(() => null);
        setSession({
          userId: body.user.id,
          email: body.user.email,
          isAdmin: me?.isAdmin ?? false,
        });
      })
      .catch((error) => setFatal((error as Error).message || 'Could not reach the Reef API.'));
  }, []);

  useEffect(() => {
    if (!session) return;
    refreshWorkflows();
    refreshConnections();
    refreshFactories();
    api.listPolicies().then((result) => setPolicies(result.policies)).catch(() => {});
    api.listSkills().then((result) => setSkills(result.skills)).catch(() => {});
    refreshInbox();
    /*
     * Whether the factory is stopped, on every screen — the fact somebody needs
     * is "nothing is running and here is why", and they will be looking at Runs
     * or the builder when they need it, wondering why their trigger did nothing.
     */
    api
      .stopSwitches()
      .then((result) => setStopped(result.live.find((entry) => entry.scope === null) ?? null))
      .catch(() => {});
  }, [session, refreshWorkflows, refreshConnections, refreshFactories, refreshInbox]);

  /*
   * The inbox is re-read on every screen change — cheap, and what keeps it
   * honest after a decision made in Slack rather than here.
   */
  useEffect(() => {
    if (!session) return;
    refreshInbox();
    if (tab === 'home' || tab === 'factories' || tab === 'work') refreshFactories();
  }, [session, tab, refreshInbox, refreshFactories]);

  /*
   * And every thirty seconds while the tab can be seen. Somebody sitting on
   * Home used to see a new approval only after they had clicked somewhere, so
   * the screen whose job is "what needs you" was the one screen that never
   * learned. Polling stops when the tab is hidden — a wall of background tabs
   * each asking every thirty seconds is load for a number nobody is looking
   * at — and the visibility change itself triggers a read, so the number is
   * right the moment the tab is looked at again rather than thirty seconds
   * later.
   */
  useEffect(() => {
    if (!session) return;
    const tick = () => {
      if (document.visibilityState === 'visible') refreshInbox();
    };
    const timer = window.setInterval(tick, 30_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [session, refreshInbox]);

  const [railWidth, setRailWidth] = usePaneWidth('reef.rail', 208);
  const [railCollapsed, toggleRail] = useRailCollapsed();
  /*
   * Below 760px the rail is a drawer under the topbar rather than a column, so
   * it needs an open state. Closed again whenever the screen changes: a drawer
   * that stayed over the screen you just chose would hide the thing you went
   * there for behind the menu you used to get there.
   */
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => setMenuOpen(false), [tab]);

  /** ⌘\ / Ctrl+\ toggles the rail between labels and icons, the way editors hide their sidebar. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key === '\\') {
        event.preventDefault();
        toggleRail();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggleRail]);

  /*
   * Split by who can fix it, because the two halves are fixed by different
   * people and a count that mixes them is a count nobody can act on. On a fresh
   * clone the old single filter said "17 connections to authorize" to a member
   * who could authorize three of them: the rest were bot credentials only an
   * admin can supply, or reef's own loopbacks that nobody authorizes at all.
   *
   * `needsYou` also insists on a `connectUrl`: a personal half with no link to
   * offer is not something a Connect button can clear, so counting it would
   * send someone to a screen with nothing for them to press.
   */
  const needsYou = connections.filter(
    (connection) => connection.usesUser && !connection.userLinked && connection.connectUrl !== null,
  );
  const needsAdmin = connections.filter((connection) => connection.usesBot && !connection.botReady);
  const inboxCount = inbox?.count ?? 0;

  /** Everything the palette can jump to. Rebuilt when the lists it draws on change. */
  const commands = useMemo<Command[]>(() => {
    const list: Command[] = [];
    for (const group of NAV) {
      for (const item of group.items) {
        list.push({
          id: `tab:${item.tab}`,
          label: item.label,
          group: 'Screen',
          icon: item.icon,
          keywords: item.hint,
          run: () => openTab(item.tab),
        });
      }
    }
    const sectionLabels: Array<[Tab, readonly { id: string; label: string }[], IconName]> = [
      ['inbox', [{ id: 'all', label: 'All' }, { id: 'review', label: 'Review Queue' }, { id: 'approvals', label: 'Approvals' }, { id: 'knowledge', label: 'Knowledge' }], 'inbox'],
      ['build', [{ id: 'run', label: 'Run a workflow' }, { id: 'language', label: 'Build with language' }], 'flow'],
      ['insights', INSIGHT_SECTIONS, 'chart'],
      ['settings', SETTINGS_SECTIONS, 'sliders'],
    ];
    for (const [sectionTab, sections, icon] of sectionLabels) {
      for (const section of sections) {
        list.push({
          id: `section:${sectionTab}:${section.id}`,
          label: section.label,
          group: NAV.flatMap((group) => group.items).find((item) => item.tab === sectionTab)?.label ?? sectionTab,
          icon,
          run: () => go({ tab: sectionTab, section: section.id }),
        });
      }
    }
    list.push({
      id: 'new-work-item',
      label: 'New work item',
      group: 'Action',
      icon: 'plus',
      keywords: 'start file describe ticket',
      run: () => go({ tab: 'work', runId: 'new' }),
    });
    for (const factory of factories) {
      list.push({
        id: `factory:${factory.id}`,
        label: factory.name,
        group: 'Factory',
        icon: 'factory',
        keywords: factory.description,
        run: () => go({ tab: 'factories', factoryId: factory.id }),
      });
    }
    for (const workflow of workflows) {
      list.push({
        id: `workflow:${workflow.id}`,
        label: workflow.name,
        group: 'Workflow',
        icon: 'flow',
        keywords: workflow.id,
        run: () => go({ tab: 'build', workflowId: workflow.id }),
      });
    }
    for (const connection of connections) {
      list.push({
        id: `connection:${connection.id}`,
        label: connection.name,
        group: 'Connection',
        icon: 'link',
        keywords: connection.id,
        run: () => go({ tab: 'connections', connectionId: connection.id }),
      });
    }
    return list;
  }, [factories, workflows, connections, go, openTab]);

  /**
   * What the palette asks the Worker for once the query is specific enough.
   *
   * Each hit arrives with the route it opens at, decided server-side; this only
   * turns it into a command. The kind-prefixed id matches the static list's
   * (`workflow:<id>`) on purpose, so a workflow found both ways is one row.
   */
  const searchCommands = useCallback(
    async (query: string): Promise<Command[]> => {
      const { hits } = await api.search(query);
      const list: Command[] = [];
      for (const hit of hits) {
        const { tab, ...rest } = hit.route;
        // A tab this build does not know is a hit it cannot open. Dropped
        // rather than sent to Home, which would read as the palette having
        // found something and then lost it.
        if (!isTab(tab)) continue;
        const target: Partial<Route> & { tab: Tab } = { ...rest, tab };
        list.push({
          id: `${hit.kind}:${hit.id}`,
          label: hit.label,
          detail: hit.detail,
          group: hit.kind === 'run' ? 'Run' : hit.kind === 'item' ? 'Work item' : 'Workflow',
          icon: hit.kind === 'run' ? 'play' : hit.kind === 'item' ? 'work' : 'flow',
          run: () => go(target),
        });
      }
      return list;
    },
    [go],
  );

  const section = sectionShown(route);
  const factoryWorkflowIds = useMemo(
    () => [...new Set(factories.flatMap((factory) => factory.stages.map((stage) => stage.workflowId)))],
    [factories],
  );

  return (
    <div
      className={`app ${railCollapsed ? 'rail-collapsed' : ''}`}
      /*
       * One variable drives both the shell's rail column and the topbar's brand
       * column, so the two cannot disagree: a brand column still 208px wide
       * over a 52px rail would put the ⌘K field over the first screen title.
       */
      style={{ '--rail-width': railCollapsed ? '52px' : `${railWidth}px` } as CSSProperties}
    >
      <header className="topbar">
        <button
          className="ghost icon-button rail-menu"
          aria-label="Menu"
          aria-expanded={menuOpen}
          aria-controls="rail"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Icon name="menu" />
        </button>
        <button className="brand" onClick={() => openTab('home')} aria-label="Reef">
          <span className="brand-mark" aria-hidden="true" />
          <span className="brand-name">Reef</span>
        </button>

        <button className="search-trigger" onClick={() => setPaletteOpen(true)} aria-label="Go to (⌘K)">
          <Icon name="search" />
          <span>Go to…</span>
          <kbd>⌘K</kbd>
        </button>

        <span className="spacer" />

        {stopped && (
          <button
            className="pill danger"
            title={`${stopped.reason} — engaged by ${stopped.engagedBy ?? 'an operator'}`}
            onClick={() => go({ tab: 'settings', section: 'operations' })}
          >
            the factory is stopped
          </button>
        )}
        {needsYou.length > 0 && (
          <button
            className="pill warn"
            title={needsYou.map((entry) => entry.name).join(', ')}
            onClick={() => openTab('connections')}
          >
            {needsYou.length} account{needsYou.length === 1 ? '' : 's'} to connect
          </button>
        )}
        {/*
          Quieter, and admins only: a member cannot set a Worker secret, so
          telling them about it is a number they can do nothing with. Neutral
          rather than warn because the warn pill beside it is the one that is
          theirs — two amber pills would read as one blocker counted twice.
        */}
        {session?.isAdmin && needsAdmin.length > 0 && (
          <button
            className="pill"
            title={needsAdmin.map((entry) => entry.name).join(', ')}
            onClick={() => openTab('connections')}
          >
            {needsAdmin.length} credential{needsAdmin.length === 1 ? '' : 's'} to set
          </button>
        )}

        {session && (
          <button
            className="primary topbar-new"
            aria-label="New work item"
            title="New work item"
            onClick={() => go({ tab: 'work', runId: 'new' })}
          >
            <Icon name="plus" /> <span className="topbar-new-label">New work item</span>
          </button>
        )}

        <button
          className="ghost icon-button"
          aria-label={`Theme: ${theme}. Switch`}
          title={`Theme: ${theme}`}
          onClick={() => setTheme(theme === 'system' ? 'dark' : theme === 'dark' ? 'light' : 'system')}
        >
          <Icon name={theme === 'light' ? 'sun' : 'moon'} />
        </button>

        {session && (
          <span className="who" title={session.email}>
            <span className="avatar" aria-hidden="true">
              {session.email.slice(0, 1).toUpperCase()}
            </span>
            <span className="hint">{session.email}</span>
          </span>
        )}
      </header>

      <div className={`shell ${railCollapsed ? '' : 'resizable'}`}>
        <nav
          id="rail"
          className={`rail ${railCollapsed ? 'collapsed' : ''}`}
          aria-label="Sections"
          data-open={menuOpen}
        >
          {NAV.map((group) => (
            <Fragment key={group.group}>
              <p className="rail-group">{group.group}</p>
              {group.items.map((item) => {
                const count = item.tab === 'inbox' ? inboxCount : item.tab === 'connections' ? needsYou.length : 0;
                return (
                  <button
                    key={item.tab}
                    className="rail-item"
                    aria-current={tab === item.tab}
                    /*
                     * The count is a chip on screen and a parenthesis to a
                     * screen reader, so "Inbox (2)" is still the name of the
                     * control however you are reading it.
                     */
                    aria-label={count > 0 ? `${item.label} (${count})` : item.label}
                    /*
                     * Collapsed, the label is what the hover has to supply —
                     * the hint alone over a bare icon would explain a button
                     * without ever naming it.
                     */
                    title={railCollapsed ? item.label : item.hint}
                    onClick={() => openTab(item.tab)}
                  >
                    <Icon name={item.icon} className="rail-icon" />
                    <span className="rail-label">{item.label}</span>
                    {count > 0 && <span className={`rail-count ${item.tab === 'connections' ? 'warn' : ''}`}>{count}</span>}
                  </button>
                );
              })}
            </Fragment>
          ))}
          <span className="spacer" />
          <a
            className="rail-item rail-link"
            href="https://github.com/onboardiq/reef/blob/main/README.md"
            target="_blank"
            rel="noreferrer"
            title={railCollapsed ? 'Documentation' : undefined}
          >
            <Icon name="book" className="rail-icon" />
            <span className="rail-label">Documentation</span>
          </a>
          {/*
            Not a `.rail-item`: the rail is asserted as ten destinations, and
            this goes nowhere. `aria-expanded` rather than a name that flips
            between Collapse and Expand, so a screen reader hears one control
            changing state rather than two controls taking turns.
          */}
          <button
            className="rail-toggle"
            aria-label="Collapse navigation"
            aria-expanded={!railCollapsed}
            aria-keyshortcuts="Meta+\ Control+\"
            title={railCollapsed ? 'Expand navigation (⌘\\)' : 'Collapse navigation (⌘\\)'}
            onClick={toggleRail}
          >
            <Icon name="chevron" className="rail-icon" />
            <span className="rail-label">Collapse</span>
          </button>
        </nav>

        {/*
          No handle on a collapsed rail: 52px is the whole point of it, and a
          drag that widened an icons-only column would leave labels hidden in
          a rail wide enough to show them.
        */}
        {!railCollapsed && (
          <PaneResizer
            label="Navigation width"
            edge="left"
            width={railWidth}
            min={140}
            max={400}
            reset={208}
            onChange={setRailWidth}
          />
        )}

        <div className="workspace">
          {fatal ? (
            <div className="main">
              <div className="banner danger">
                <h3>Reef is unreachable</h3>
                <p style={{ margin: 0 }}>{fatal}</p>
              </div>
            </div>
          ) : !session ? (
            <div className="main">
              <div className="empty">Signing you in with Okta…</div>
            </div>
          ) : tab === 'device' ? (
            /*
             * Behind the session gate, deliberately. Approving a terminal grants
             * it this account's access, so the approval has to be made by
             * somebody who has proved they are that account — an unauthenticated
             * version of this screen would be a page that hands out sessions to
             * whoever opens it.
             */
            <DeviceView />
          ) : tab === 'home' ? (
            <HomeView
              factories={factories}
              connections={connections}
              inbox={inbox}
              isAdmin={session.isAdmin}
              onDecided={refreshInbox}
              stopped={stopped !== null}
              go={go}
            />
          ) : tab === 'factories' ? (
            <FactoriesView factories={factories} factoryId={route.factoryId} go={go} />
          ) : tab === 'work' ? (
            <WorkView factories={factories} runId={route.runId} userId={session.userId} go={go} />
          ) : tab === 'inbox' ? (
            <InboxView
              section={section}
              item={route.item}
              inbox={inbox}
              isAdmin={session.isAdmin}
              onSection={openSection}
              onDecided={refreshInbox}
              go={go}
            />
          ) : tab === 'build' ? (
            <WorkflowsView
              section={section}
              workflows={workflows}
              workflowId={route.workflowId}
              connections={connections}
              policies={policies}
              skills={skills}
              factories={factories}
              factoryWorkflowIds={factoryWorkflowIds}
              userId={session.userId}
              go={go}
              onSection={openSection}
              onOpenWorkflow={openWorkflow}
              onWorkflowsChanged={refreshWorkflows}
            />
          ) : tab === 'agents' ? (
            <AgentsView />
          ) : tab === 'connections' ? (
            <ConnectionsView
              connections={connections}
              factories={factories}
              connectionId={route.connectionId}
              isAdmin={session.isAdmin}
              onRefresh={refreshConnections}
              onOpenConnection={openConnection}
              onOpenWorkflow={leaveForWorkflow}
            />
          ) : tab === 'runs' ? (
            <RunsView
              workflows={workflows}
              workflowId={route.workflowId}
              runId={route.runId}
              stepId={route.stepId}
              onOpenRun={openRun}
            />
          ) : tab === 'insights' ? (
            <InsightsView section={section} workflows={workflows} isAdmin={session.isAdmin} onSection={openSection} />
          ) : (
            <SettingsView
              section={section}
              policies={policies}
              isAdmin={session.isAdmin}
              onSection={openSection}
              onPoliciesChanged={() =>
                api.listPolicies().then((result) => setPolicies(result.policies)).catch(() => {})
              }
            />
          )}
        </div>
      </div>

      <CommandPalette
        open={paletteOpen}
        commands={commands}
        search={searchCommands}
        onClose={() => setPaletteOpen(false)}
      />
    </div>
  );
}
